import * as z from "zod/v4";
import {
  type UIMessage as AIUIMessage,
  type StepResult,
  type ToolSet,
  type UIDataTypes,
  type UITools,
} from "ai";
import {
  type ReasoningUIPart,
  type TextUIPart,
  type UIMessage,
  type UIMessagePart,
} from "./ui-message.ts";
import { isEmpty } from "../util/text.ts";
import { map } from "@gordonb/generator";
import { monotonicUlid } from "@std/ulid";

/**
 * Validate UIMessageParts' validity.
 * A UIMessagePart array must have at least one text part with non-empty text to
 * be convertible to a valid Anthropic API message.
 */
export const partsHaveValidTextPart = (parts: UIMessagePart[]): boolean =>
  parts.some(
    (part) => part.type === "text" && !isEmpty((part as TextUIPart).text),
  );

/**
 * Validate UI message's validity.
 * A UIMessage must have at least one text part with non-empty text to be
 * convertible to a valid Anthropic API message.
 */
export const hasValidTextPart = (message: {
  parts: UIMessagePart[];
}): boolean => partsHaveValidTextPart(message.parts);

/**
 * Check whether any part is a tool part (tool-invocation, tool-result, etc.).
 * Tool-only messages are valid for persistence: they survive pruning and
 * convertToModelMessages converts them into tool_use/tool_result blocks.
 */
export const partsHaveToolPart = (parts: UIMessagePart[]): boolean =>
  parts.some((part) => part.type.startsWith("tool-"));

/**
 * Check whether a message has persistable content.
 * The rules mirror the Anthropic API's per-role constraints:
 * - User messages must have text (tool-only user messages are nonsensical).
 * - Assistant messages need text OR tool parts (tool-only is valid, but
 *   reasoning-only is not — reasoning is stripped during pruning, leaving an
 *   empty message that poisons the API).
 */
export const hasValidContent = (message: {
  role: string;
  parts: UIMessagePart[];
}): boolean => {
  if (message.role === "user") return partsHaveValidTextPart(message.parts);
  return (
    partsHaveValidTextPart(message.parts) || partsHaveToolPart(message.parts)
  );
};

/** Given UIMessageParts, get the text-y parts and flatten them into a single string */
export const flattenUiPartsToString = (parts: UIMessagePart[]): string =>
  parts
    .map((part) => {
      if (part.type === "text") return (part as TextUIPart).text;
      if (part.type === "reasoning") {
        return `(Thinking: ${(part as ReasoningUIPart).text})`;
      }
      return undefined;
    })
    .filter((part) => part !== undefined)
    .join("\n");

/** Given an array of messages, flatten to a string. */
export const flattenUIMessagesToString = (
  messages: Iterable<UIMessage>,
): string => {
  const flattenedMessages = map(
    messages,
    (message) => `${message.role}:\n${flattenUiPartsToString(message.parts)}`,
  );
  return Array.from(flattenedMessages).join("\n\n");
};

/**
 * Lenient schema for sanitizing a message part loaded from DB.
 * JSON.stringify drops undefined values, so fields like `input` on tool parts
 * can go missing. The Anthropic API requires `input` on `server_tool_use`
 * blocks. This preprocess step applies a default `input: {}` for tool parts.
 */
export const DbUIMessagePartSchema = z.preprocess((val) => {
  if (
    typeof val === "object" &&
    val !== null &&
    "type" in val &&
    typeof val.type === "string" &&
    val.type.startsWith("tool-")
  ) {
    return { input: {}, ...val };
  }
  return val;
}, z.any());

/** Construct a minimal user UIMessage with a single text part. */
export const userMessage = <
  Metadata = unknown,
  DataParts extends UIDataTypes = UIDataTypes,
  Tools extends UITools = UITools,
>(
  text: string,
): AIUIMessage<Metadata, DataParts, Tools> => ({
  id: monotonicUlid(),
  role: "user",
  parts: [{ type: "text", text }],
});

/** Check if any step made a tool call with the given name. */
export const hasToolCallNamed = <TOOLS extends ToolSet>(
  steps: StepResult<TOOLS>[],
  name: string,
): boolean =>
  steps.some((step) => step.toolCalls.some((call) => call.toolName === name));

/** Return all tool calls with the given name across all steps. */
export const listToolCallsNamed = <TOOLS extends ToolSet>(
  steps: StepResult<TOOLS>[],
  name: string,
) =>
  steps.flatMap((step) =>
    step.toolCalls.filter((call) => call.toolName === name),
  );
